import type { QueryClient } from '@tanstack/react-query';
import { AuditQueryKeys } from '../api/keys';
import { isVersionConflictError } from './versionConflict';

/**
 * Sidecar cache for the per-question `lastUpdatedAt`
 * optimistic-concurrency token. The `GetAuditSummary` SDK type
 * intentionally does not carry a record-level token, so we keep it
 * in a separate cache slot populated by the polling merger.
 *
 * Values are epoch-millis strings — the exact wire format the legacy
 * REST `UpdateAuditQuestionResponseRequest.expectedLastUpdatedAt`
 * field accepts. Storing in wire format keeps the mutation-side
 * enrichment a single assignment with no conversion.
 */
export type QuestionResponseTokenMap = Readonly<Record<string, string>>;

/** Query key for the sidecar. Same audit-scoping as the summary. */
export function questionResponseTokensKey(auditId: string): readonly unknown[] {
  return [AuditQueryKeys.responseTokens, { auditId }] as const;
}

/**
 * Read the cached lastUpdatedAt token for a single question.
 * Returns undefined when the sidecar is empty (fresh page-load,
 * pre-first-poll) or the question has never been observed.
 */
export function readCachedResponseToken(
  queryClient: QueryClient,
  auditId: string,
  questionId: string,
): string | undefined {
  const map = queryClient.getQueryData<QuestionResponseTokenMap>(
    questionResponseTokensKey(auditId),
  );
  // eslint-disable-next-line security/detect-object-injection -- questionId is a well-defined mutation identifier, not free-form user input
  return map?.[questionId];
}

/**
 * Mutation-side enrichment: sets `expectedLastUpdatedAt` on the
 * variables when the sidecar has a token for this question.
 * No-op otherwise (fresh load / pre-first-poll) — server accepts
 * the write under the optional-for-backward-compat contract.
 *
 * Mutates the variables object in place so the mutationFn sees the
 * enriched payload without needing to receive the QueryClient
 * separately (createMutation's mutationFn is variables-only).
 *
 * The cast to `{ expectedLastUpdatedAt?: string }` bridges the SDK
 * type (Date-typed) to the wire type (epoch-millis string). The
 * mutation hook casts the same variables through to the legacy REST
 * request type at the wire boundary; both casts refer to the same
 * bytes on the JSON line.
 */
export function enrichVariablesWithExpectedToken(
  queryClient: QueryClient,
  variables: { readonly auditId: string; readonly questionId: string },
): void {
  const token = readCachedResponseToken(
    queryClient,
    variables.auditId,
    variables.questionId,
  );
  // eslint-disable-next-line security/detect-possible-timing-attacks -- comparing to undefined; the token value is a public epoch-millis string, no secret data
  if (token === undefined) return;
  (variables as unknown as { expectedLastUpdatedAt?: string }).expectedLastUpdatedAt =
    token;
}

/**
 * Update the sidecar with a batch of polled tokens. Skips entries
 * whose value matches the current cache — avoids allocating a new
 * object when nothing changed.
 *
 * The poll response is authoritative for tokens: whatever value the
 * server returned wins, no newest-wins gating. Same-user bypass on
 * the server tolerates a token temporarily going stale immediately
 * after a local write (see docblock on the mutation hook).
 */
/**
 * Post-conflict token refresh: when the server rejects a write
 * with a 409, the error carries the `actualVersion` — the fresh
 * `lastUpdatedAt` the server currently holds. Write that into the
 * sidecar so the next attempt against the same row uses the
 * up-to-date token instead of the (now-stale) polled value.
 *
 * No-op when the error isn't a version conflict or when
 * `actualVersion` is absent from the modeled body.
 */
export function refreshCachedResponseTokenFromConflict(
  queryClient: QueryClient,
  auditId: string,
  questionId: string,
  error: unknown,
): void {
  if (!isVersionConflictError(error)) return;
  const { actualVersion } = error;
  if (actualVersion === undefined) return;
  writeCachedResponseTokens(queryClient, auditId, [[questionId, actualVersion]]);
}

export function writeCachedResponseTokens(
  queryClient: QueryClient,
  auditId: string,
  updates: readonly (readonly [questionId: string, lastUpdatedAt: string])[],
): void {
  if (updates.length === 0) return;
  const key = questionResponseTokensKey(auditId);
  const previous = queryClient.getQueryData<QuestionResponseTokenMap>(key) ?? {};
  let next: Record<string, string> | undefined;
  for (const [questionId, lastUpdatedAt] of updates) {
    // eslint-disable-next-line security/detect-object-injection -- questionId is a well-defined mutation identifier, not free-form user input
    if (previous[questionId] === lastUpdatedAt) continue;
    if (!next) next = { ...previous };
    // eslint-disable-next-line security/detect-object-injection -- questionId is a well-defined mutation identifier
    next[questionId] = lastUpdatedAt;
  }
  if (next) queryClient.setQueryData(key, next);
}
